import { Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { client } from "../../../App";
import { getValidationRule, handlerErrors } from "../../../helpers/helpers";
import {
  getEvictionForMutation,
  OPERATOR_UPDATE_EMAIL,
} from "../../../helpers/mutations";
import { hideLoading, showLoading } from "../../../reducers/app";
import { manageReloadSearchUser } from "../../../reducers/auth";
import Modal from "../../common/Modal/Modal";
import "./ModifyEmail.scss";

const ModifyEmail = () => {
  const searchUser = useSelector((state) => state.auth.searchUser);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [icon, setIcon] = useState("");
  const [error, setError] = useState(false);
  const [errorNoUsername, setErrorNoUsername] = useState(false);
  const [text, setText] = useState("");
  const [success, setSuccess] = useState(false);
  const [sendData, setSendData] = useState({});
  const getValidationSchema = () => {
    const validationEmail = getValidationRule("email");
    return Yup.object().shape({
      email: Yup.string()
        .matches(validationEmail, t("errors.registration.public.email.match"))
        .required(t("common.required.input")),
      confirmEmail: Yup.string()
        .required(t("common.required.input"))
        .oneOf(
          [Yup.ref("email"), null],
          t("errors.registration.public.confirmEmail.equal")
        ),
    });
  };
  const history = useHistory();

  useEffect(() => {
    if (searchUser === null) {
      setErrorNoUsername(true);
      setShow(true);
    }
    if (confirm) {
      setIcon("gc-icon-functional-attention-2-col");
      setText(t("operator.modify_email.page.confirm"));
    }
    if (error) {
      setIcon("gc-icon-functional-close-circle-2-col");
    }
    if (errorNoUsername) {
      setIcon("gc-icon-functional-close-circle-2-col");
      setText(t("operator.modify_email.page.error_noUsername"));
    }
    if (success) {
      setIcon("gc-icon-functional-check-2-col");
      setText(t("operator.modify_email.page.success"));
    }
  }, [confirm, error, success, searchUser, errorNoUsername, t]);
  const confirmSubmit = (values) => {
    setShow(true);
    setConfirm(true);
    setSendData(values);
  };
  const updateEmail = () => {
    setShow(false);
    dispatch(showLoading());
    client()
      .mutate({
        mutation: OPERATOR_UPDATE_EMAIL,
        variables: {
          req: { username: sendData.username, newEmail: sendData.email },
        },
        update: (cache) => {
          const fieldsToEvict = getEvictionForMutation("adminUpdateEmail");
          fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
          cache.gc();
        },
      })
      .then(() => {
        setSuccess(true);
        setConfirm(false);
        setShow(true);
        dispatch(
          manageReloadSearchUser({ email: sendData.email, status: true })
        );
        dispatch(hideLoading());
      })
      .catch((e) => {
        const errorMessages = handlerErrors(e, setText, setText);

        if (errorMessages.length > 0) {
          const firstKey = Object.keys(errorMessages[0])[0];
          setText(errorMessages[0][firstKey]);
        } else {
          setText(t("operator.modify_email.page.error"));
        }
        setConfirm(false);
        setError(true);
        dispatch(hideLoading());
        setShow(true);
      });
  };
  return (
    <div className="ModifyEmail mt-5 mb-5" data-testid="ModifyEmail">
      <div className="container">
        <Formik
          initialValues={{
            username: searchUser ? searchUser.email : "",
            email: "",
            confirmEmail: "",
          }}
          validationSchema={getValidationSchema()}
          onSubmit={(values) => confirmSubmit(values)}
        >
          {({ errors, touched, values, setFieldValue, submitCount }) => (
            <Form noValidate>
              <div className="row">
                <div className="col-md-6 col-xs-12">
                  <label className="paragraph-small-fs9">
                    {t("operator.modify_email.page.oldEmail.label")}
                  </label>
                  <p className="mb-3">{searchUser ? searchUser.email : ""}</p>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 col-xs-12">
                  <div
                    className={
                      errors.email && touched.email && submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label className="paragraph-small-fs9">
                      {t("operator.modify_email.page.email.label") + " *"}
                    </label>
                    <Field
                      name={"email"}
                      type="email"
                      id="email"
                      aria-describedby={
                        errors.email && touched.email && submitCount > 0
                          ? "email-error"
                          : null
                      }
                      required={true}
                      className={
                        errors.email && touched.email && submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      placeholder={t(
                        "operator.modify_email.page.email.placeholder"
                      )}
                      onCopy={(e) => e.preventDefault()}
                    />
                    {errors.email && touched.email && submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"email-error"}
                      >
                        {errors.email}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="col-md-6 col-xs-12">
                  <div
                    className={
                      errors.confirmEmail &&
                      touched.confirmEmail &&
                      submitCount > 0
                        ? "form-group has-error"
                        : "form-group"
                    }
                  >
                    <label className="paragraph-small-fs9">
                      {t("operator.modify_email.page.confirmEmail.label") +
                        " *"}
                    </label>
                    <Field
                      name={"confirmEmail"}
                      type="email"
                      id="confirmEmail"
                      className={
                        errors.confirmEmail &&
                        touched.confirmEmail &&
                        submitCount > 0
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      aria-describedby={
                        errors.confirmEmail &&
                        touched.confirmEmail &&
                        submitCount > 0
                          ? "confirmEmail-error"
                          : null
                      }
                      required={true}
                      placeholder={t(
                        "operator.modify_email.page.confirmEmail.placeholder"
                      )}
                      onPaste={(e) => e.preventDefault()}
                    />
                    {errors.confirmEmail &&
                    touched.confirmEmail &&
                    submitCount > 0 ? (
                      <p
                        className="form-error-text paragraph-small-form"
                        role="alert"
                        id={"confirmEmail-error"}
                      >
                        {errors.confirmEmail}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 col-xs-12 text-center">
                  <button type="submit" className={"btn btn-primary"}>
                    {t("confirm.save")}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <Modal
        show={show}
        onClose={() => setShow(false)}
        icon={icon}
        descriptionClass={"paragraph-main-fs8"}
        content={text}
        type={"button"}
        onFirstClick={
          confirm
            ? () => updateEmail()
            : () => {
                setShow(false);
                history.push("/");
              }
        }
        linkTitle={
          confirm
            ? t("operator.modify_email.page.btn_confirm_ok")
            : error
            ? t("operator.modify_email.page.btn_error")
            : t("operator.modify_email.page.btn_success")
        }
        typeSecond={confirm ? "button" : null}
        onSecondClick={
          confirm
            ? () => {
                setShow(false);
                setConfirm(false);
              }
            : null
        }
        linkTitleSecond={
          confirm ? t("operator.modify_email.page.btn_confirm_ko") : null
        }
      />
    </div>
  );
};

ModifyEmail.propTypes = {};

export default ModifyEmail;
